/** Right-click / long-press menu for a Files-tab item, plus the hook that opens it. */
import { useEffect, useRef, useState } from "react";
import { Download, Pencil, Trash2, Globe, FolderOpen, Eye } from "lucide-react";

/** Returns { ctx, setCtx, handlersFor(item) } — spread handlersFor(item) on each row/card. */
export const useFileContextMenu = () => {
  const [ctx, setCtx] = useState(null); // {x, y, item}
  const timer = useRef(null);
  const fired = useRef(false);
  const handlersFor = (item) => ({
    onContextMenu: (e) => { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY, item }); },
    onTouchStart: (e) => {
      const t = e.touches[0];
      fired.current = false;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { fired.current = true; setCtx({ x: t.clientX, y: t.clientY, item }); }, 500);
    },
    onTouchMove: () => clearTimeout(timer.current),
    onTouchEnd: (e) => { clearTimeout(timer.current); if (fired.current) { e.preventDefault(); } },
    onClickCapture: (e) => { if (fired.current) { e.stopPropagation(); e.preventDefault(); fired.current = false; } },
  });
  return { ctx, setCtx, handlersFor };
};

export const FileContextMenu = ({ ctx, onClose, onOpen, onDownload, onRename, onDelete, onAddToBrowser, browserOn }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: ctx.x, top: ctx.y });
  useEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ left: Math.min(ctx.x, window.innerWidth - r.width - 8), top: Math.min(ctx.y, window.innerHeight - r.height - 8) });
    const close = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("mousedown", close); window.addEventListener("touchstart", close); window.addEventListener("keydown", esc); window.addEventListener("scroll", onClose, true);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("touchstart", close); window.removeEventListener("keydown", esc); window.removeEventListener("scroll", onClose, true); };
  }, [ctx, onClose]);
  const item = ctx.item;
  const row = "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100";
  const act = (fn) => () => { onClose(); fn(item); };
  return (
    <div ref={ref} role="menu" style={{ position: "fixed", left: pos.left, top: pos.top }} data-testid="file-context-menu"
      className="z-[200] w-56 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-2xl">
      <p className="truncate border-b border-gray-100 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">{item.name}</p>
      <button type="button" className={row} onClick={act(onOpen)} data-testid="file-ctx-open">{item.is_folder ? <FolderOpen className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {item.is_folder ? "Open folder" : "Open"}</button>
      {!item.is_folder && <button type="button" className={row} onClick={act(onDownload)} data-testid="file-ctx-download"><Download className="h-4 w-4" /> Download</button>}
      {!item.is_folder && browserOn && <button type="button" className={row} onClick={act(onAddToBrowser)} data-testid="file-ctx-add-browser"><Globe className="h-4 w-4 text-[#2c6eeb]" /> Add to browser<span className="ml-auto font-mono text-[9px] uppercase text-gray-400">files bar</span></button>}
      <button type="button" className={row} onClick={act(onRename)} data-testid="file-ctx-rename"><Pencil className="h-4 w-4" /> Rename</button>
      <div className="my-1 border-t border-gray-100" />
      <button type="button" className={`${row} text-red-600 hover:bg-red-50`} onClick={act(onDelete)} data-testid="file-ctx-delete"><Trash2 className="h-4 w-4" /> Delete…</button>
    </div>
  );
};
